import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Terms of Service | Best Christmas Gift Reviews';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Review the Terms of Service for Best Christmas Gift Reviews, including usage rules, intellectual property, affiliate links, and liability disclaimers.');

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/terms-of-service#webpage`,
      url: `${SITE_URL}/terms-of-service`,
      name: 'Terms of Service',
      description: 'Review the Terms of Service for Best Christmas Gift Reviews, including usage rules, intellectual property, affiliate links, and liability disclaimers.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${SITE_URL}/terms-of-service` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-terms';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('jsonld-terms');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1B4332] hover:text-[#8B2635] transition-colors mb-6">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-[#1B4332] mb-4">Terms of Service</h1>
            <p className="text-gray-500">Last Updated: January 2025</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using Best Christmas Gift Reviews ("the Website"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Use of Website</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You agree to use this website only for lawful purposes and in a way that does not:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Infringe on the rights of others</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Restrict or inhibit anyone else's use of the website</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Violate any applicable laws or regulations</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Attempt to gain unauthorized access to our systems</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on this website, including text, graphics, logos, images, and software, is the property of Best Christmas Gift Reviews or its content suppliers and is protected by copyright laws.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Product Reviews and Recommendations</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our reviews and recommendations are based on our research and opinions. While we strive for accuracy, we cannot guarantee that all information is complete or error-free.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Product prices, availability, and specifications may change without notice. We recommend verifying details with retailers before making purchases.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Affiliate Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website contains affiliate links to products on Amazon and other retailers. When you click these links and make purchases, we may earn commissions. This does not affect the price you pay. For more details, please see our{' '}
                <Link to="/affiliate-disclosure" className="text-[#8B2635] hover:underline">
                  Affiliate Disclosure
                </Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the content, accuracy, or practices of these external sites. Visiting third-party websites is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed">
                This website is provided "as is" without warranties of any kind, either express or implied. We do not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the fullest extent permitted by law, Best Christmas Gift Reviews shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the website or any products purchased through our affiliate links.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless Best Christmas Gift Reviews and its affiliates from any claims, damages, or expenses arising from your use of the website or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of the website after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
              </p>
            </section>

            <section className="bg-[#FFF9F5] rounded-xl p-6 border border-[#D4AF37]/20">
              <h2 className="text-xl font-bold text-[#1B4332] mb-3">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about these Terms of Service, please contact us at{' '}
                <a href="mailto:hello@bestchristmasgiftreviews.com" className="text-[#8B2635] hover:underline">
                  hello@bestchristmasgiftreviews.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
